import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  CircularProgress,
  IconButton,
  Snackbar,
  Tooltip,
  useTheme,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { API_BASE, authFetch } from "../../Utils/apiClient";
import { tokens } from "../../theme";

const Donations = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/donate/getAllDonations`);
      const json = await res.json();
      if (json.status && json.data) {
        setData(
          json.data.map((d) => ({
            id: d.id,
            fullName: d.fullName || "N/A",
            email: d.email || "N/A",
            phoneNumber: d.phoneNumber || "N/A",
            donationType: d.donationType || "N/A",
            amount: d.amount ?? 0,
            createdAt: d.createdAt
              ? new Date(d.createdAt).toLocaleString("en-US")
              : "N/A",
          }))
        );
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Failed to load donations:", err);
      setSnackbar({ open: true, severity: "error", message: "Failed to load donations" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this donation record?")) return;
    try {
      const res = await authFetch(`${API_BASE}/api/donate/deleteDonate/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setSnackbar({ open: true, severity: "success", message: "Donation deleted" });
        load();
      }
    } catch (err) {
      setSnackbar({ open: true, severity: "error", message: "Delete failed" });
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "fullName", headerName: "Donor Name", flex: 1, minWidth: 140 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 160 },
    { field: "phoneNumber", headerName: "Phone", width: 120 },
    { field: "donationType", headerName: "Type", flex: 1, minWidth: 120 },
    {
      field: "amount",
      headerName: "Amount ($)",
      width: 110,
      renderCell: (p) => `$${Number(p.value || 0).toFixed(2)}`,
    },
    { field: "createdAt", headerName: "Date", width: 170 },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <IconButton onClick={() => handleDelete(params.row.id)} size="small">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const exportPdf = () => {
    const doc = new jsPDF();
    doc.text("JCGN Donations", 14, 15);
    doc.autoTable({
      startY: 22,
      head: [["ID", "Name", "Email", "Type", "Amount", "Date"]],
      body: data.map((d) => [
        d.id,
        d.fullName,
        d.email,
        d.donationType,
        `$${Number(d.amount).toFixed(2)}`,
        d.createdAt,
      ]),
    });
    doc.save("donations.pdf");
  };

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" height="50vh" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="Donations" subtitle="All donations from jcgn.org" />
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <button
          type="button"
          onClick={exportPdf}
          style={{
            background: colors.greenAccent[600] || "#4caf50",
            color: "#fff",
            border: "none",
            padding: "8px 16px",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          Export PDF
        </button>
      </Box>
      <Box height="70vh">
        <DataGrid
          rows={data}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <MuiAlert severity={snackbar.severity}>{snackbar.message}</MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default Donations;
